import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as XLSX from 'xlsx';
import { DigisubservicesService } from '../services/digisubservices.service';

@Component({
  selector: 'app-office-rep-monthwise-report',
  templateUrl: './office-rep-monthwise-report.component.html',
  styleUrls: ['./office-rep-monthwise-report.component.scss']
})
export class OfficeRepMonthwiseReportComponent implements OnInit {

  today: Date = new Date();
  selectedYear: string = '';

  representatives: any[] = [];
  totals: any = {};
  grandYearTotal = { billing: 0, paid: 0, remaining: 0 };

  monthKeys: string[] = [];
  monthNames: any = {}; // dynamic month names, can be mapped later

  startDate: string = '';
  endDate: string = '';

  constructor(
    private service: DigisubservicesService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    // ✅ Get dates first
    if (this.data) {
      this.startDate = this.formatDate(this.data.startDate);
      this.endDate = this.formatDate(this.data.endDate);
    }

    if (!this.startDate || !this.endDate) {
      this.startDate = '2025-04-01';
      this.endDate = '2026-03-31';
    }

    // ✅ Set selected financial year
    this.setSelectedYear();

    // ✅ Fetch report
    this.fetchReportData();
  }

  // ================= FORMAT DATE =================
  formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  }

  // ================= SET FINANCIAL YEAR =================
  setSelectedYear() {
    if (!this.startDate) {
      this.selectedYear = '';
      return;
    }
    const start = new Date(this.startDate);
    if (isNaN(start.getTime())) {
      this.selectedYear = '';
      return;
    }

    const year = start.getFullYear();
    const month = start.getMonth() + 1;

    this.selectedYear = month >= 4 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
  }

  // ================= FETCH REPORT =================
  fetchReportData() {
    const payload = { startDate: this.startDate, endDate: this.endDate };
    this.service.getOfficeRepMonthwiseReport(payload).subscribe({
      next: (res: any) => {
        if (!res?.report || res.report.length === 0) return;

        // Dynamic month keys
        this.monthKeys = Object.keys(res.report[0]).filter(
          k => k !== 'sr_no' && k !== 'office_rep_name' && !k.includes('Year')
        );

        // Map month names (optional)
        this.monthKeys.forEach(key => {
          this.monthNames[key] = key; // replace with readable mapping if needed
        });

        // Map representatives and calculate per-rep totals
        this.representatives = res.report.map((item: any) => {
          const mapped: any = { name: item.office_rep_name };
          let billing = 0, paid = 0, remaining = 0;

          this.monthKeys.forEach(month => {
            const b = Number(item[month]?.billing_amt) || 0;
            const p = Number(item[month]?.paid_amt) || 0;
            const r = Number(item[month]?.unpaid_amt) || 0;

            mapped[month] = { billing: b, paid: p, remaining: r };

            billing += b;
            paid += p;
            remaining += r;
          });

          mapped.yearTotal = { billing, paid, remaining };
          return mapped;
        });

        // Calculate month totals
        this.calculateTotals(res.grand_totals);

        // Calculate grand year total
        this.calculateYearGrandTotal();
      },
      error: err => console.error('Error fetching report data', err)
    });
  }

  // ================= CALCULATE MONTH TOTALS =================
  calculateTotals(grandTotalsFromApi?: any) {
    this.totals = {};
    this.monthKeys.forEach(month => {
      if (grandTotalsFromApi?.[month]) {
        this.totals[month] = {
          billing: Number(grandTotalsFromApi[month].billing_amt) || 0,
          paid: Number(grandTotalsFromApi[month].paid_amt) || 0,
          remaining: Number(grandTotalsFromApi[month].unpaid_amt) || 0
        };
      } else {
        this.totals[month] = { billing: 0, paid: 0, remaining: 0 };
        this.representatives.forEach(rep => {
          this.totals[month].billing += rep[month]?.billing || 0;
          this.totals[month].paid += rep[month]?.paid || 0;
          this.totals[month].remaining += rep[month]?.remaining || 0;
        });
      }
    });
  }

  // ================= CALCULATE YEAR GRAND TOTAL =================
  calculateYearGrandTotal() {
    this.grandYearTotal = { billing: 0, paid: 0, remaining: 0 };
    this.representatives.forEach(rep => {
      this.grandYearTotal.billing += rep.yearTotal.billing;
      this.grandYearTotal.paid += rep.yearTotal.paid;
      this.grandYearTotal.remaining += rep.yearTotal.remaining;
    });
  }

  // ================= EXPORT TO EXCEL =================
  exportExcel(filename: string) {
    const element = document.getElementById('print-section');
    if (!element) return;
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'OfficeRep Report');
    XLSX.writeFile(wb, `${filename}.xlsx`);
  }

  // ================= PRINT =================
printReportA3Landscape() {
  const headerElement = document.querySelector('.report-container > table') as HTMLTableElement;
  const printSection = document.getElementById('print-section') as HTMLElement;

  if (!headerElement || !printSection) return;

  const contentWidth = Math.max(printSection.scrollWidth, headerElement.scrollWidth);
  const contentHeight = headerElement.scrollHeight + printSection.scrollHeight;

  const MM_TO_PX = 3.7795;
  const MARGIN_MM = 10;
  const A3_PRINT_WIDTH  = Math.floor((420 - MARGIN_MM * 2) * MM_TO_PX);
  const A3_PRINT_HEIGHT = Math.floor((297 - MARGIN_MM * 2) * MM_TO_PX);

  const scaleX = A3_PRINT_WIDTH  / contentWidth;
  const scaleY = A3_PRINT_HEIGHT / contentHeight;
  const scale  = Math.min(scaleX, scaleY, 1);

  const scaledHeight = contentHeight * scale;

  const headerRows = headerElement.querySelectorAll('tr');
  const row0 = headerRows[0]?.querySelectorAll('td');
  const row1 = headerRows[1]?.querySelectorAll('td');

  const r0c0 = row0?.[0]?.innerHTML ?? '';
  const r0c1 = row0?.[1]?.innerHTML ?? '';
  const r0c2 = row0?.[2]?.innerHTML ?? '';
  const r1c0 = row1?.[0]?.innerHTML ?? '';
  const r1c1 = row1?.[1]?.innerHTML ?? '';
  const r1c2 = row1?.[2]?.innerHTML ?? '';

  const popupWin = window.open('', '_blank', 'width=1600,height=1200');
  if (!popupWin) return;

  popupWin.document.open();
  popupWin.document.write(`
    <html>
      <head>
        <title>Business Report</title>
        <style>
          @page {
            size: A3 landscape;
            margin: ${MARGIN_MM}mm;
          }

          * {
            box-sizing: border-box;
          }

          html, body {
            margin: 0;
            padding: 0;
            width: ${A3_PRINT_WIDTH}px;
            font-family: Arial, sans-serif;
            overflow: hidden;
          }

          .print-wrapper {
            width: ${A3_PRINT_WIDTH}px;
            height: ${scaledHeight}px;
            overflow: hidden;
          }

          .print-container {
            transform: scale(${scale});
            transform-origin: top left;
            width: ${contentWidth}px;
          }

          /* ── Header ── */
          .header-block {
            width: 100%;
            border-collapse: collapse;
            border: 2px solid black;
            background-color: #f8f9fa;
            margin-bottom: 0;
          }

          .header-block tr:first-child td {
            padding: 8px;
            font-size: 16px;
            border-bottom: 1px solid black;
            border-right: 1px solid black;
            background-color: #f8f9fa;
          }

          .header-block tr:first-child td:last-child {
            border-right: none;
          }

          .header-block tr:last-child td {
            padding: 6px 8px;
            font-size: 14px;
            border-right: 1px solid black;
            background-color: #eef2f7;
          }

          .header-block tr:last-child td:last-child {
            border-right: none;
          }

          /* ── Data table ── */
          table {
            border-collapse: collapse;
            width: 100%;
            font-size: 11px;
          }

          th, td {
            border: 1px solid black;
            padding: 4px;
            text-align: center;
            white-space: nowrap;
          }

          thead tr:first-child th {
            background-color: #f5c518;
          }

          thead tr:last-child th {
            background-color: #fff9e6;
          }

         
            tfoot tr th {
  background-color: #c8753f !important;
  color: #000 !important;
  font-weight: bold;

  /* 🔥 VERY IMPORTANT */
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

          thead {
            display: table-header-group;
          }

          tr {
            page-break-inside: avoid;
          }

          #print-section {
            overflow: visible !important;
            border: none !important;
            margin-top: 0 !important;
          }
        </style>
      </head>

      <body onload="window.print(); window.close();">
        <div class="print-wrapper">
          <div class="print-container">

            <!-- HEADER -->
            <table class="header-block" style="width:${contentWidth}px;">
              <tr>
                <td style="font-size:18px; font-weight:bold; text-align:center; width:33%;">
                  ${r0c0}
                </td>
                <td style="font-size:20px; font-weight:bold; text-align:center; width:34%;">
                  ${r0c1}
                </td>
                <td style="font-size:16px; text-align:right; font-weight:bold; width:33%;">
                  ${r0c2}
                </td>
              </tr>
              <tr>
                <td style="font-size:16px; font-weight:bold; text-align:center; width:33%;">
                  ${r1c0}
                </td>
                <td style="font-size:16px; font-weight:bold; text-align:center; width:34%;">
                  ${r1c1}
                </td>
                <td style="font-size:14px; text-align:right; font-weight:bold; width:33%;">
                  ${r1c2}
                </td>
              </tr>
            </table>

            <!-- DATA TABLE -->
            ${printSection.outerHTML}

          </div>
        </div>
      </body>
    </html>
  `);

  popupWin.document.close();
}
}